import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { apiClient } from "../../apiclient/apiclient";
import {
  ArrowLeft,
  Hash,
  User,
  UserCheck,
  Calendar,
  Wallet,
  Tag,
  CircleDollarSign,
  Scale,
  Users,
} from "lucide-react";
import { toast } from "react-toastify";
import ThemedTable from "../../components/ThemedTable";

const ActivityTypeLabel = {
  order_accepted: "✅ Order Accepted",
  item_collected: "📦 Item Collected",
  in_transit: "� Shipped / In Transit",
  delivered: "📍 Delivered",
  completed: "🏁 Completed",
  payment_collected: "💰 Payment Collected",
};

const ActivityTypeColor = {
  order_accepted: "bg-orange-100 border-orange-300",
  item_collected: "bg-green-100 border-green-300",
  in_transit: "bg-blue-100 border-blue-300",
  delivered: "bg-red-100 border-red-300",
  completed: "bg-emerald-100 border-emerald-300",
  payment_collected: "bg-yellow-100 border-yellow-300",
};

const StatusColors = {
  pending: "bg-yellow-100 text-yellow-700",
  processing: "bg-blue-100 text-blue-700",
  shipped: "bg-purple-100 text-purple-700",
  delivered: "bg-green-100 text-green-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

const formatAnyDateToDDMMYYYY = (dateStr) => {
  if (!dateStr) return "N/A";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-IN");
};

function TrackOrder() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const orderId = searchParams.get("id");

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orderId) {
      setError("Order ID not provided");
      setLoading(false);
      return;
    }

    const fetchOrder = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get(`/orders/${orderId}`);
        setOrder(response.data.order);
        setError(null);
      } catch (err) {
        console.error("Error fetching order:", err);
        setError(err.response?.data?.message || "Failed to load order");
        toast.error("Failed to load order details");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || "Order not found"}</p>
          <button
            onClick={() => navigate(-1)}
            className="bg-blue-900 hover:bg-orange-500 text-white px-4 py-2 rounded-lg transition"
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  const activities = (order.activities || []).sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
  );
  const hasActivities = activities.length > 0;

  return (
    <div className="min-h-screen ">
      {/* Header */}
      <div className="w-full border-b pt-3 ps-4 pb-4 border-slate-200 bg-blue-50 sticky top-0 z-10">
        <div className="flex items-center gap-4 pe-4">
          <button
            onClick={() => navigate(-1)}
            className="p-1 hover:bg-white rounded-lg transition"
            title="Go back"
          >
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-slate-800">
              {order.orderId}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {order.clientId?.name && <span>{order.clientId.name}</span>}
              {order.clientId?.name && order.deliveryPersonId?.name && (
                <span> • </span>
              )}
              {order.deliveryPersonId?.name && (
                <span>Delivery: {order.deliveryPersonId.name}</span>
              )}
            </p>
          </div>
          <div className="flex gap-2 items-center">
            {/* <span
              className={`px-3 py-1 rounded-full text-xs font-semibold capitalize ${StatusColors[order.status] || "bg-gray-100 text-gray-700"}`}
            >
              {order.status || "pending"}
            </span> */}
            <span className="px-3 py-1 rounded-full text-xs text-orange-500 font-semibold bg-white border-2 border-orange-500">
              {order.activities?.length || 0} Steps
            </span>
          </div>
        </div>
      </div>

      {/* Order Information Section */}
      <div className="w-full bg-white border-b p-5 border-slate-200 flex flex-wrap md:flex-nowrap justify-around items-center gap-6">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-1.5 text-slate-400">
            <User size={14} />
            <span className="text-[10px] font-semibold uppercase tracking-wider">
              Client
            </span>
          </div>
          <p
            className="font-bold text-slate-900 text-sm truncate max-w-[200px]"
            title={order.clientId?.name}
          >
            {order.clientId?.name || "N/A"}
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-1.5 text-slate-400">
            <UserCheck size={14} />
            <span className="text-[10px] font-semibold uppercase tracking-wider">
              Agent
            </span>
          </div>
          <p className="font-bold text-slate-900 text-sm">
            {order.deliveryPersonId?.name || "N/A"}
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-1.5 text-slate-400">
            <Users size={14} />
            <span className="text-[10px] font-semibold uppercase tracking-wider">
              Reference
            </span>
          </div>
          <p className="font-bold text-slate-900 text-sm">
            {order.items?.some((i) => i.staffReference?.name)
              ? [...new Set(order.items.map((i) => i.staffReference?.name).filter(Boolean))].join(", ")
              : (order.staffReference?.name || "Direct Sales (No Agent)")}
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-1.5 text-slate-400">
            <Calendar size={14} />
            <span className="text-[10px] font-semibold uppercase tracking-wider">
              Date
            </span>
          </div>
          <p className="font-bold text-slate-900 text-sm">
            {formatAnyDateToDDMMYYYY(order.createdAt)}
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-1.5 text-slate-400">
            <div
              className={`w-2 h-2 rounded-full ${StatusColors[order.status]?.split(" ")[0] || "bg-gray-400"}`}
            />
            <span className="text-[10px] font-semibold uppercase tracking-wider">
              Status
            </span>
          </div>
          <p
            className={`text-sm font-bold capitalize ${StatusColors[order.status]?.split(" ")[1] || "text-gray-700"}`}
          >
            {order.status || "pending"}
          </p>
        </div>
      </div>

      {/* Items Section */}
      <div className="w-full border-y py-2 px-4 border-slate-200 bg-blue-50 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-tight">
            Order Items
          </h3>
          <p className="text-[10px] text-slate-500 font-medium">
            Detailed list of products and quantities in this shipment
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto">
        <div className="bg-white border-b">
          {order.items && order.items.length > 0 ? (
            <div className="overflow-x-auto">
              <ThemedTable className="text-left border-collapse w-full">
                <thead>
                  <tr>
                    <th className="w-1/12 text-center">#</th>
                    <th className="w-4/12">Product Name</th>
                    <th className="w-2/12 text-left">Reference</th>
                    <th className="w-1/12 text-center">Quantity</th>
                    <th className="w-1/12 text-center">Unit</th>
                    <th className="w-1.5/12 text-right">Price</th>
                    <th className="w-1.5/12 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((item, idx) => (
                    <tr key={idx}>
                      <td className="text-center font-medium">{idx + 1}</td>
                      <td className="font-semibold text-slate-800">
                        <div className="flex flex-col">
                          <span>{item.productName}</span>
                          <span className="text-[10px] text-slate-500 font-medium mt-0.5">
                            {item.warehouseName || "No Warehouse"}
                            {item.expiryDate && ` (Exp: ${new Date(item.expiryDate).toLocaleDateString()})`}
                            {item.batchId && item.batchId !== "N/A" && ` [Batch: ${item.batchId}]`}
                          </span>
                        </div>
                      </td>
                      <td className="text-left">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${item.staffReference?.name ? "bg-purple-50 text-purple-700 border border-purple-100" : "bg-slate-50 text-slate-600 border border-slate-200"}`}>
                          {item.staffReference?.name || order.staffReference?.name || "Direct Sales (No Agent)"}
                        </span>
                      </td>
                      <td className="text-center">{item.quantity}</td>
                      <td className="text-center">
                        {item.quantityUnit || "N/A"}
                      </td>
                      <td className="text-right">
                        ₹{Number(item.price || 0).toLocaleString()}
                      </td>
                      <td className="text-right font-bold text-slate-900">
                        ₹
                        {(
                          Number(item.quantity || 0) * Number(item.price || 0)
                        ).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </ThemedTable>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No items in this order</p>
          )}
        </div>
      </div>

      {/* Payment Details Section */}
      <div className="w-full border-y py-2 px-4 border-slate-200 bg-blue-50 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-tight">
            Payment Details
          </h3>
          <p className="text-[10px] text-slate-500 font-medium">
            Financial summary including totals, discounts, and outstanding
            balance
          </p>
        </div>
      </div>

      {/* Payment Details Section Bar */}
      <div className="w-full bg-white border-b p-5 border-slate-200 flex flex-wrap md:flex-nowrap justify-around items-center gap-6">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-1.5 text-slate-400">
            <CircleDollarSign size={14} />
            <span className="text-[10px] font-semibold uppercase tracking-wider">
              Total Amount
            </span>
          </div>
          <p className="font-bold text-slate-900 text-sm">
            ₹{Number(order.paymentDetails?.totalAmount || 0).toLocaleString()}
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-1.5 text-slate-400">
            <Tag size={14} />
            <span className="text-[10px] font-semibold uppercase tracking-wider">
              Discount
            </span>
          </div>
          <p className="font-bold text-orange-600 text-sm">
            ₹{Number(order.paymentDetails?.discount || 0).toLocaleString()}
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-1.5 text-slate-400">
            <Wallet size={14} />
            <span className="text-[10px] font-semibold uppercase tracking-wider">
              Paid Amount
            </span>
          </div>
          <p className="font-bold text-green-600 text-sm">
            ₹{Number(order.paymentDetails?.paidAmount || 0).toLocaleString()}
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-1.5 text-slate-400">
            <Scale size={14} />
            <span className="text-[10px] font-semibold uppercase tracking-wider">
              Balance Due
            </span>
          </div>
          <p
            className={`text-sm font-bold ${Number(order.paymentDetails?.balanceAmount || 0) > 0 ? "text-red-600" : "text-green-600"}`}
          >
            ₹{Number(order.paymentDetails?.balanceAmount || 0).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Vehicle Image Section */}
      {order.shipmentDetails?.type === "vehicle" &&
        order.shipmentDetails?.vehicleImageUrl && (
          <>
            <div className="w-full border-y py-2 px-4 border-slate-200 bg-blue-50 flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-tight flex items-center gap-2">
                  <span>🚗</span> Vehicle Information
                </h3>
                <p className="text-[10px] text-slate-500 font-medium">
                  Visual proof of shipment and vehicle transit details
                </p>
              </div>
            </div>
            <div className="max-w-6xl mx-auto">
              <div className="bg-white border-b p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <img
                      src={`https://starnutrition.onrender.com${order.shipmentDetails.vehicleImageUrl}`}
                      alt="Vehicle"
                      className="w-full h-64 object-cover rounded-lg shadow-sm"
                    />
                  </div>
                  <div className="flex flex-col justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">
                        Shipment Details
                      </h3>
                      <p className="text-sm text-gray-600 mb-1">
                        <span className="font-medium">Type:</span>{" "}
                        {order.shipmentDetails.type === "vehicle"
                          ? "🚗 Vehicle Delivery"
                          : order.shipmentDetails.type}
                      </p>
                      {order.shipmentDetails.vehicleImageUploadedAt && (
                        <p className="text-sm text-gray-600">
                          <span className="font-medium">Uploaded:</span>{" "}
                          {new Date(
                            order.shipmentDetails.vehicleImageUploadedAt,
                          ).toLocaleString("en-IN")}
                        </p>
                      )}
                    </div>
                    {order.assignedBy && (
                      <div className="text-xs text-gray-500 border-t pt-2 mt-2">
                        <p className="font-medium">Assigned to:</p>
                        <p>{order.assignedBy?.name || "Delivery Partner"}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

      {/* Activities Timeline with Maps */}
      <div className="w-full border-y py-2 px-4 border-slate-200 bg-blue-50 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-slate-800 uppercase tracking-tight">
            Delivery Tracking Timeline
          </h3>
          <p className="text-[10px] text-slate-500 font-medium">
            Visual log of location updates and delivery milestones
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        {hasActivities ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 p-4">
            {activities.map((activity, idx) => (
              <div
                key={idx}
                className="bg-white border border-slate-200 overflow-hidden flex flex-col group transition-all duration-300"
              >
                {/* Card Header */}
                <div className="px-4 py-3 border-b flex items-center justify-between bg-white">
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-2 h-2 rounded-full ${ActivityTypeColor[activity.type]?.split(" ")[0].replace("bg-", "bg-") || "bg-slate-300"}`}
                    />
                    <span className="text-xs font-bold text-slate-700 uppercase tracking-tight">
                      {ActivityTypeLabel[activity.type] || activity.type}
                    </span>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                    Step {idx + 1}
                  </span>
                </div>

                {/* Map Section */}
                <div className="relative aspect-video w-full border-b border-slate-100">
                  <iframe
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    style={{ border: 0 }}
                    src={`https://maps.google.com/maps?q=${activity.latitude},${activity.longitude}&z=15&output=embed`}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                  {/* Subtle coordinates overlay on hover */}
                  <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/10 transition-colors pointer-events-none flex items-end p-2">
                    <p className="text-[8px] text-white font-mono opacity-0 group-hover:opacity-100 transition-opacity">
                      {activity.latitude.toFixed(4)},{" "}
                      {activity.longitude.toFixed(4)}
                    </p>
                  </div>
                </div>

                {/* Card Footer - Timestamp */}
                <div className="px-4 py-3 bg-slate-50/50 flex items-center justify-between">
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <Calendar size={12} />
                    <span className="text-[11px] font-medium">
                      {new Date(activity.timestamp).toLocaleDateString("en-IN")}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <span className="text-[11px] font-bold text-slate-900">
                      {new Date(activity.timestamp).toLocaleTimeString(
                        "en-IN",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                        },
                      )}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center h-64">
            <div className="text-center text-gray-500">
              <p className="text-lg">📍 No location activities recorded yet</p>
              <p className="text-sm mt-2">
                Activities will appear here as delivery partner progresses
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TrackOrder;
